import { useDispatch } from "react-redux";
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getAllTenantFieldOptions } from "../../../../actions/Tenants/actions";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { usePagination } from "../../../../hooks";
import { callAvosApi } from "../../../../utils/useAvosApiHook";
import { ImportExportModal } from "../../../Helper/ImportExportModal/index";
import { MailRecipients } from "../../Helpers";
import { getEmailRecipients, getTableRow, validateImportItems } from "../utils";
import { PageBase } from "../PageBase";
import { defineSupplierFormCreate } from "./defineSupplierFormCreate";
import { defineSupplierInfoFormCreate } from "./defineSupplierInfoFormCreate";
import { defineGrowerRelationFormCreate } from "./defineGrowerRelationFormCreate";
import { Supplier } from "./types";
import { useImportExport } from "../../../Helper/ImportExportModal/useImportExport";

const endpoint = "/supplier/";

const SupplierPage = () => {
    const dispatch = useDispatch();
    const config = useConfig();
    const queryClient = useQueryClient();

    const { offset, limit } = usePagination();

    const importFields = [
        { label: "name", description: "Supplier name", required: true },
        { label: "label", required: true, copyValueFrom: "name" },
        {
            label: "country",
            description: <span>Only countries configured in <Link to={`/tenants/${config?.tenant_id}/form-field-options/country`}>country form options</Link> are valid.</span>,
            required: true,
        },
        {
            label: "fruit_types",
            description: <span>Only fruit types configured in <Link to={`/tenants/${config?.tenant_id}/form-field-options/fruit_type`}>fruit type form options</Link> are valid. Separate multiple values with comma.</span>,
            required: true,
        },
        { label: "report_type", description: "Report types (multiple values separated by comma). Leaving it empty will send all report types." },
        { label: "emails", description: "Emails (multiple values separated by comma)" },
        { label: "ggn", description: `Grower's GGN. Fill in at least one fruit type.` },
    ];

    const entityName = "supplier";
    const modalProps = useImportExport({
        entityName,
        importFields,
        validateImportItems: ({ importData, existingItems }) => validateImportItems(entityName, { importData, existingItems, config }),
    });

    const getSuppliers = async (offset, limit) => {
        const res = await callAvosApi(`${endpoint}?limit=${limit}&offset=${offset}`);
        return res.data;
    };

    const { data: suppliers, isLoading } = useQuery({
        queryKey: ["suppliers", offset, limit],
        queryFn: () => getSuppliers(offset, limit),
    });

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: ["suppliers"], exact: false });
        queryClient.invalidateQueries({ queryKey: ["all-suppliers"], exact: false });
    };

    const { mutate: saveSupplier } = useMutation({
        mutationFn: async (supplier: Supplier) => {
            return callAvosApi(`${endpoint}${supplier.id ?? ""}`, {
                method: supplier.id ? "PUT" : "POST",
                data: supplier,
            });
        },
        onSuccess: () => {
            invalidateQueries();
        },
    });

    const { mutate: deleteSupplier } = useMutation({
        mutationFn: async (id: string) => {
            return callAvosApi(`${endpoint}${id}`, {
                method: "DELETE",
            });
        },
        onSuccess: () => {
            invalidateQueries();
        },
    });

    const supplierState = useMemo(() => {
        return {
            items: suppliers || { results: [], count: 0 },
            isLoading,
            filter: { offset, limit },
            localFiltering: false,
            reload: false
        };
    }, [suppliers, isLoading, offset, limit]);

    const onSaveSupplier = async (supplier: Supplier) => {
        await saveSupplier(supplier);
        dispatch(getAllTenantFieldOptions(config.tenant_id) as any);
    };

    const onDeleteSupplier = async (id: string) => {
        await deleteSupplier(id);
        dispatch(getAllTenantFieldOptions(config.tenant_id) as any);
    };

    const tableRow = [
        ...getTableRow(config, "Supplier"),
        {
            label: "Email Recipients",
            name: "email_recipients",
            display_component(entity: Supplier) {
                const email_recipients = getEmailRecipients(entity);
                return <MailRecipients recipients={email_recipients} />;
            },
            lg: 3,
        }
    ];

    const createSupplierForm = defineSupplierFormCreate(onSaveSupplier, onDeleteSupplier);
    const createSupplierInfoForm = defineSupplierInfoFormCreate(onSaveSupplier);
    const createSupplierRelationForm = defineGrowerRelationFormCreate(onSaveSupplier);
    const createForms = [createSupplierInfoForm, createSupplierRelationForm, createSupplierForm];
    const defaultModalIndex = createForms.indexOf(createSupplierForm);

    return <PageBase<Supplier>
        title="Supplier"
        tableColumns={tableRow}
        actionsColumnWidth={3}
        createForms={createForms as any}
        state={supplierState}
        defaultModalIndex={defaultModalIndex}
        actions={<ImportExportModal entityName={entityName} importFields={importFields} {...modalProps} />}
    />;
};

export default SupplierPage;
